import { z } from 'zod';
import { sessionModeSchema } from './agent.types.js';
import { metadataChangeSchema } from './realtime.js';
import { stateDeltaSchema } from './tool-results.types.js';
export const createConversationRequestSchema = z.object({
    title: z.string().min(1),
    sessionMode: sessionModeSchema,
    modelId: z.string(),
    // NEW — cockpit-lovable-alignment Phase 2. Optional creator-intent metadata
    // captured at create time via <NewDealModal> (Phase 4 consumer); persisted to
    // agent_state.dealMetadata. Request-side names use targetIrr/targetYoc (full
    // names); response side uses irr/yoc (short aliases) — intentional asymmetry
    // per research/phase-2.md § 11 R1.
    dealMetadata: z
        .object({
        market: z.string().min(1).max(255).optional(),
        units: z.number().int().positive().optional(),
        basis: z
            .string()
            .regex(/^\d+(\.\d+)?$/)
            .optional(),
        targetIrr: z
            .string()
            .regex(/^\d+(\.\d+)?$/)
            .optional(),
        targetYoc: z
            .string()
            .regex(/^\d+(\.\d+)?$/)
            .optional(),
    })
        .optional(),
});
export const sendMessageRequestSchema = z.object({
    content: z.string().min(1),
});
export const ingestModelRequestSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    version: z.string().optional(),
});
export const conversationStatusSchema = z.enum(['active', 'completed']);
export const conversationResponseSchema = z.object({
    id: z.string(),
    userId: z.string(),
    title: z.string(),
    status: conversationStatusSchema,
    sessionMode: sessionModeSchema,
    modelId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    // NEW — cockpit-lovable-alignment Phase 2. All text because JSONB `->>`
    // returns text (research/phase-2.md § 4.8 + § 11 R3); pre-feature deals
    // (no `dealMetadata` sub-key on agent_state) return null for all 5.
    // Response-side field names: irr/yoc (short — asymmetric vs request side's
    // targetIrr/targetYoc per § 11 R1).
    market: z.string().nullable().optional(),
    units: z.string().nullable().optional(),
    basis: z.string().nullable().optional(),
    irr: z.string().nullable().optional(),
    yoc: z.string().nullable().optional(),
});
/**
 * Response shape for `POST /api/conversations/:id/fork`. The
 * `orphanedInputCount` field is the number of inputs from the source
 * conversation's `agent_state.inputValues` that did NOT carry over to the new
 * conversation (either the input was removed in the target schema, or its
 * `dataType` changed). Web reads this to populate the fork-success banner.
 */
export const forkConversationResponseSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    modelId: z.string(),
    modelVersion: z.string(),
    orphanedInputCount: z.number().int().nonnegative(),
});
/**
 * Request body for `POST /api/conversations/:id/model-edit` —
 * in-platform-model-view Phase 1 Session 1 (2026-04-25).
 *
 * `editId` is UUIDv4 (matches the wire-format `editOpSchema.uuid()` validation
 * in `realtime.ts`). The web layer generates it via `crypto.randomUUID()`
 * before sending. Foundation's `recordEdit()` uses the field for idempotency
 * — replaying the same `(conversationId, editId)` returns the original DiffOp
 * without side effects.
 *
 * NOTE: the `agents/CLAUDE.md` doc references "use UUIDv7 for editId" — that
 * aspiration is not currently enforced by `editOpSchema` (which accepts any
 * RFC4122 UUID) and conflicts with `crypto.randomUUID()`'s output. UUIDv4 is
 * the de-facto contract; the docs drift was flagged in spec pass 5 / 7.
 */
export const modelEditRequestSchema = z.object({
    namedRange: z.string().min(1),
    value: z.unknown(),
    editId: z.string().uuid(),
    /**
     * Google Places `place_id` — only populated for `dataType:'location'` inputs.
     * Absence ⇒ plain string edit, no resolver call, no paid Google request (ADR 0017).
     * conversation-model-versioning Phase 6 Session 4, ADR 0027.
     */
    placeId: z.string().optional(),
    /**
     * Google Places `AutocompleteSessionToken` serialised via JSON.stringify.
     * Optional — resolver succeeds without it (cache-first, ADR 0017).
     * conversation-model-versioning Phase 6 Session 4, ADR 0027.
     */
    sessionToken: z.string().optional(),
    /**
     * Provenance source — market-defaults-application Phase 4 fix (2026-06-10).
     *
     * Absent (or `'user'`) ⇒ existing manual-edit behaviour preserved byte-for-byte:
     * `recordEdit` calls `recordUserInput()` → USER→CONFIRMED/OVERRIDDEN transition.
     *
     * `'market_data'` ⇒ one-click-apply picker provenance. `recordEdit` calls
     * `tracker.record()` with `source:'market_data'` so the value lands with honest
     * provenance rather than `source:'user'`. Requires the web layer to forward
     * `source:'market_data'` when the value originates from the market-data cache
     * (e.g. `<MarketDataApplyPanel>` → `useApplyValue`).
     *
     * Only 'user' and 'market_data' are accepted at this endpoint. Other
     * `ProvenanceSource` values ('default', 'calculated', 'scenario', 'inferred')
     * are agent-internal and MUST NOT be forwarded by the web layer.
     */
    source: z.enum(['user', 'market_data']).optional(),
    /**
     * Optional provenance detail fields — mirror the optional fields on
     * `ProvenanceRecord`. Only meaningful when `source === 'market_data'`.
     * All fields are additive and back-compat; absent ⇒ undefined on the record.
     */
    /** Data-provider display name (e.g. `'RentCast'`, `'FRED'`). */
    sourceName: z.string().optional(),
    /** Canonical URL of the data source. */
    sourceUrl: z.string().optional(),
    /** ISO-8601 date string indicating when the source data was valid. */
    dataAsOf: z.string().optional(),
    /**
     * Confidence score 0..1. When `source === 'market_data'` and this field is
     * absent, the service defaults to `0.9` (sensible floor for applied market data).
     * When `source === 'user'` (or absent), the service always uses `1.0`.
     */
    confidence: z.number().min(0).max(1).optional(),
    /**
     * Optional freeform note from the user — forwarded UNCONDITIONALLY to
     * `provenance.reasoning` → `record_edit_txn.p_source_note` →
     * `model_edits.source_note` for both `source:'user'` and `source:'market_data'`
     * callers. Absent ⇒ no `reasoning` key written (back-compat preserved).
     * (market-default-apply-ux Phase 1 Session 1.3; api-local mirror adds `.openapi()` in 1.5)
     */
    note: z.string().optional(),
});
/**
 * Request body for `POST /api/conversations/:id/table-edit` —
 * table-input-ux feature (2026-06-08).
 *
 * Enables a direct, LLM-free write of a full table input (e.g. unit mix) via
 * the `propose_table_fill` confirm flow. Mirrors `modelEditRequestSchema`'s
 * idempotency contract: `editId` is UUIDv4, generated by the web layer via
 * `crypto.randomUUID()` and forwarded to `recordEdit()` in the service layer.
 *
 * D1 (numeric-only v1): `values` is `Record<string, number>` — date/boolean/
 * location columns are proposable-not-writable and rendered READ-ONLY in the
 * grid. The schema widens in a future pass if non-numeric table inputs ship.
 *
 * The response reuses `modelEditResponseSchema`; `stateDelta.written` carries
 * COMPOSITE-CELL NAMED-RANGE keys (e.g. `Studio_Rent_Month`), NOT display
 * paths (`UnitMix.Studio.rent`), per the cross-domain contract in design.md § 7.
 */
export const tableEditRowSchema = z.object({
    label: z.string().min(1),
    values: z.record(z.string(), z.number()), // D1: numeric-only v1
});
export const tableEditRequestSchema = z.object({
    table: z.string().min(1),
    rows: z.array(tableEditRowSchema).min(1),
    editId: z.string().uuid(),
});
/**
 * Response shape for `POST /api/conversations/:id/model-edit`.
 *
 * 2026-05-26 — widened with `stateDelta` + `metadataChanges` to drive the
 * client-side optimistic-patch consolidation (Option C). The server commits
 * inputProvenance + dealMetadata + aerialLatLng atomically via the v3
 * record_edit_txn RPC and returns the full delta so the web cache can patch
 * synchronously without waiting for a refetch round-trip.
 *
 * Field semantics:
 *   - `editId` / `revision` / `prevRevision` / `appliedAt` — diff envelope identifiers
 *   - `appliedOps` / `computedChanges` — v1 diff payload, retained for back-compat
 *   - `provenance` — the post-edit ProvenanceRecord for the SINGLE edited input
 *     (legacy field, predates stateDelta — kept for callers that read it directly)
 *   - `systemMessageId` — id of the appended `[model-view:*]` SystemMessage, or
 *     `null` if the append failed (durable edit still committed)
 *   - `stateDelta` — the canonical 4-field shape mirrored from set_inputs:
 *     `{written, recomputed, currentProvenance, pendingDefaults}`. For a single-
 *     input edit, `written` and `currentProvenance` are single-entry records;
 *     `recomputed` carries downstream outputs the engine recomputed; `pendingDefaults`
 *     is empty for model-edit (no batch defaults flip).
 *   - `metadataChanges` — mutations to `dealMetadata` + `aerialLatLng` (empty array
 *     for plain scalar edits; populated for location edits). Same shape as DiffOpV2.
 *   - `dealMetadata` — convenience projection of the post-edit dealMetadata map
 *     for fast cache patch (web optimistic merge reads this directly)
 *   - `aerialLatLng` — convenience projection of the post-edit aerialLatLng
 */
export const modelEditResponseSchema = z.object({
    editId: z.string().uuid(),
    revision: z.number().int().positive(),
    prevRevision: z.number().int().nonnegative(),
    appliedOps: z.array(z.unknown()),
    computedChanges: z.array(z.unknown()),
    appliedAt: z.string(),
    systemMessageId: z.string().nullable(),
    provenance: z.unknown().nullable(),
    stateDelta: stateDeltaSchema,
    metadataChanges: z.array(metadataChangeSchema),
    /**
     * Full ProvenanceRecord map for every input the server touched (the edited
     * input + any derived sub-attrs like `Location.coordinates` /
     * `Location.censusTract`). Typed as `unknown` here to avoid pulling the
     * Zod-runtime `provenanceRecordSchema` into the api.types module's import
     * graph; consumers narrow at the call site. Distinct from
     * `stateDelta.currentProvenance` which carries only the narrow
     * `{source, defaultStatus?, confidence?}` shape mirrored from set_inputs.
     */
    inputProvenance: z.record(z.string(), z.unknown()),
    dealMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
    aerialLatLng: z
        .object({
        lat: z.number(),
        lng: z.number(),
    })
        .nullable()
        .optional(),
});
/**
 * Response shape for `GET /api/conversations/:id/active-version-sync`.
 * Indicates whether the live conversation's inputs are in sync with the
 * snapshot captured in the current underwriting version (builder-ux-overhaul
 * Phase 2, ADR 0001). Used by the Phase-4 web `useActiveVersionDrift` hook
 * to drive the drift pill + share gate.
 *
 * Field semantics:
 * - `hasVersion`: `current_version_id != null`
 * - `inSync`: `canonicalEqual(deriveInputValues(live), deriveInputValues(frozen))`;
 *   explicitly `false` (NOT null) when `hasVersion === false`
 * - `currentVersionId`: the `current_version_id` pointer; `null` when no version
 * - `currentVersionLabel`: sourced from `conversation_versions.label` (NOT
 *   `.version_label` which is the unrelated `underwriting_events` column)
 */
export const activeVersionSyncSchema = z.object({
    hasVersion: z.boolean(),
    inSync: z.boolean(),
    currentVersionId: z.string().uuid().nullable(),
    currentVersionLabel: z.string().nullable(),
});
